import "./editrate.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "src/API2";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcArrow  = () => <Svg><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></Svg>;
const IcPlus   = () => <Svg><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const IcClose  = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcEdit   = () => <Svg size={14}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></Svg>;
const IcTrash  = () => <Svg size={14}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></Svg>;
const IcUser   = () => <Svg size={14}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcUsers  = () => <Svg size={14}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></Svg>;
const IcKid    = () => <Svg size={14}><circle cx="12" cy="7" r="3" /><path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /></Svg>;
const IcCal    = () => <Svg size={14}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>;
const IcCheck  = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcEmpty  = () => <Svg size={32}><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;

/* ── Helpers ────────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};
const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Math.round(Number(n) || 0);
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " DA";
};
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const PRICE_FIELDS = [
  { key: "price_1", label: "1 adult",     icon: <IcUser /> },
  { key: "price_2", label: "2 adults",    icon: <IcUsers /> },
  { key: "price_3", label: "3 adults",    icon: <IcUsers /> },
  { key: "price_4", label: "4 adults",    icon: <IcUsers /> },
  { key: "price_5", label: "5 adults",    icon: <IcUsers /> },
  { key: "price_extra", label: "Extra adult", icon: <IcUser /> },
  { key: "price_child", label: "Extra child", icon: <IcKid /> },
];

/* ── Add-room-type modal ────────────────────────────────────────── */
const AddRoomType = ({ setActive, rateId, existingRoomIds }) => {
  const [prices, setPrices] = useState({
    price_1: 0, price_2: 0, price_3: 0, price_4: 0, price_5: 0,
    price_extra: 0, price_child: 0,
  });
  const [roomType, setRoomType] = useState("");
  const [roomTypes, setRoomTypes] = useState([]);

  useEffect(() => {
    API.get("rooms").then((resp) => {
      const available = (resp.data.rooms || []).filter((r) => r.available === true);
      setRoomTypes(available);
    });
  }, []);

  const setPriceField = (key, val) => setPrices((p) => ({ ...p, [key]: val }));

  const post = () => {
    if (!roomType) { toast.error("Select a room type"); return; }
    toast.promise(
      API.post("rates/" + rateId, { ...prices, room_id: roomType }).then(() => {
        setTimeout(() => { setActive(false); window.location.reload(); }, 400);
      }),
      { loading: "Saving…", success: "Room type added", error: "Could not save" },
    );
  };

  return (
    <div className="er-modal-overlay" onClick={() => setActive(false)}>
      <div className="er-modal" onClick={(e) => e.stopPropagation()}>
        <header className="er-modal__head">
          <div>
            <span className="er-modal__eyebrow">Pricing</span>
            <h3>Add room type to rate</h3>
          </div>
          <button className="er-icon-btn" onClick={() => setActive(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>
        <div className="er-modal__body">
          <label className="er-field">
            <span>Room type</span>
            <select value={roomType} onChange={(e) => setRoomType(e.target.value)}>
              <option value="" disabled>Select room type</option>
              {roomTypes.map((r) => (
                <option key={r.id} value={r.id} disabled={existingRoomIds.includes(r.id)}>
                  {r.code}{existingRoomIds.includes(r.id) ? " — already in rate" : ""}
                </option>
              ))}
            </select>
          </label>

          <div className="er-price-grid">
            {PRICE_FIELDS.map((f) => (
              <label className="er-field" key={f.key}>
                <span>{f.label}</span>
                <div className="er-input-money">
                  <input
                    inputMode="numeric"
                    value={prices[f.key]}
                    onChange={(e) => setPriceField(f.key, e.target.value)}
                    placeholder="15000"
                  />
                  <span className="er-input-money__unit">DA</span>
                </div>
              </label>
            ))}
          </div>
        </div>
        <footer className="er-modal__foot">
          <button className="er-btn er-btn--ghost" onClick={() => setActive(false)}>Cancel</button>
          <button className="er-btn er-btn--primary" onClick={post}>
            <IcCheck /> Add room type
          </button>
        </footer>
      </div>
    </div>
  );
};

/* ── Main page ──────────────────────────────────────────────────── */
const EditRates = () => {
  const { rateId } = useParams();
  const navigate = useNavigate();

  const [rate, setRate] = useState(null);
  const [rateRoomTypes, setRateRoomTypes] = useState([]);
  const [selected, setSelected] = useState(0);
  const [activeModal, setActiveModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [prices, setPrices] = useState({
    price_1: 0, price_2: 0, price_3: 0, price_4: 0, price_5: 0,
    price_extra: 0, price_child: 0,
  });

  /* Load rate */
  useEffect(() => {
    API.get(`rates/${rateId}`)
      .then((resp) => {
        setRate(resp.data.rate);
        const available = (resp.data.rate.details || []).filter(
          (d) => d.room?.available === true,
        );
        setRateRoomTypes(available);
      })
      .catch((e) => console.error(e));
  }, [rateId]);

  /* When the user picks a different room type, prefill the price form */
  useEffect(() => {
    const cur = rateRoomTypes[selected];
    if (!cur) return;
    setPrices({
      price_1: cur.price_1 ?? 0,
      price_2: cur.price_2 ?? 0,
      price_3: cur.price_3 ?? 0,
      price_4: cur.price_4 ?? 0,
      price_5: cur.price_5 ?? 0,
      price_extra: cur.price_extra ?? 0,
      price_child: cur.price_child ?? 0,
    });
  }, [selected, rateRoomTypes]);

  const setPriceField = (key, val) => setPrices((p) => ({ ...p, [key]: val }));

  const workingDays = useMemo(() => {
    if (!rate?.work_days) return [];
    try { return JSON.parse(rate.work_days); } catch { return []; }
  }, [rate]);

  const status = useMemo(() => {
    if (!rate) return null;
    const now = new Date();
    const s = new Date(rate.start_date);
    const e = new Date(rate.end_date);
    if (e < now) return { key: "expired", label: "Expired" };
    if (s > now) return { key: "upcoming", label: "Upcoming" };
    return { key: "active", label: "Live" };
  }, [rate]);

  const existingRoomIds = useMemo(
    () => rateRoomTypes.map((d) => d.room?.id).filter(Boolean),
    [rateRoomTypes],
  );

  const saveSelected = () => {
    const target = rateRoomTypes[selected];
    if (!target) return;
    setSubmitting(true);
    toast.promise(
      API.put(`rates/details/${target.id}`, prices).finally(() => setSubmitting(false)),
      { loading: "Saving…", success: "Pricing saved", error: "Could not save" },
    );
  };

  const deleteDetail = (detail) => {
    if (!window.confirm(`Remove ${detail.room?.code} from this rate?`)) return;
    toast.promise(
      API.delete(`rates/details/${detail.id}`).then(() => {
        setTimeout(() => window.location.reload(), 400);
      }),
      { loading: "Removing…", success: "Removed", error: "Could not remove" },
    );
  };

  if (!rate) {
    return (
      <div className="er-page">
        <div className="er-loading">Loading rate…</div>
      </div>
    );
  }

  const typeKey = (rate.type || "RATE").toLowerCase();

  return (
    <div className="er-page">
      {/* ── Hero ── */}
      <header className="er-hero">
        <button type="button" className="er-back" onClick={() => navigate("/rates")}>
          <IcArrow /> Back to rates
        </button>
        <div className="er-hero__body">
          <div>
            <span className="er-hero__eyebrow">Pricing matrix</span>
            <h1 className="er-hero__title">
              {rate.code}
              <span className={`er-type er-type--${typeKey}`}>{rate.type}</span>
              {status && <span className={`er-status er-status--${status.key}`}>{status.label}</span>}
            </h1>
            <p className="er-hero__sub">
              Pick a room type on the left to edit its per-occupancy prices, or add a new room type to this rate.
            </p>
          </div>
          <button
            type="button"
            className="er-btn er-btn--on-dark"
            onClick={() => navigate(`/rates/update/${rateId}`)}
          >
            <IcEdit /> Edit rate details
          </button>
        </div>

        {/* Meta strip inside hero */}
        <div className="er-meta">
          <div className="er-meta__item">
            <span><IcCal /> Validity</span>
            <strong>{fmtDate(rate.start_date)} → {fmtDate(rate.end_date)}</strong>
          </div>
          <div className="er-meta__item er-meta__item--days">
            <span><IcCal /> Working days</span>
            <div className="er-day-strip">
              {DAY_LABELS.map((d, i) => (
                <span key={i} className={`er-day ${workingDays.includes(i) ? "is-on" : ""}`}>
                  {d}
                </span>
              ))}
            </div>
          </div>
          <div className="er-meta__item">
            <span>Room types</span>
            <strong>{rateRoomTypes.length}</strong>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="er-grid">
        {/* Left: room types */}
        <aside className="er-types">
          <header className="er-types__head">
            <div>
              <h3>Room types</h3>
              <p>{rateRoomTypes.length} configured</p>
            </div>
            <button
              type="button"
              className="er-btn er-btn--primary"
              onClick={() => setActiveModal(true)}
            >
              <IcPlus /> Add type
            </button>
          </header>

          {rateRoomTypes.length === 0 ? (
            <div className="er-empty">
              <IcEmpty />
              <h4>No room types yet</h4>
              <p>Add a room type to start pricing it under this rate.</p>
              <button
                type="button"
                className="er-btn er-btn--primary"
                onClick={() => setActiveModal(true)}
              >
                <IcPlus /> Add first type
              </button>
            </div>
          ) : (
            <ul className="er-type-list">
              {rateRoomTypes.map((d, i) => (
                <li
                  key={d.id}
                  className={`er-type-row ${i === selected ? "is-active" : ""}`}
                  onClick={() => setSelected(i)}
                >
                  <span className="er-type-row__num">{i + 1}</span>
                  <div className="er-type-row__body">
                    <strong>{d.room?.code || "—"}</strong>
                    <span>{d.room?.description?.slice(0, 60) || "No description"}</span>
                    <span className="er-type-row__price">
                      from {fmtMoney(d.price_1)} · {fmtMoney(d.price_2)} (2p)
                    </span>
                  </div>
                  <div className="er-type-row__actions">
                    <button
                      className="er-icon-btn"
                      onClick={(e) => { e.stopPropagation(); window.location.href = "/rooms/update/" + d.room?.id; }}
                      title="Open room type"
                    >
                      <IcEdit />
                    </button>
                    <button
                      className="er-icon-btn er-icon-btn--danger"
                      onClick={(e) => { e.stopPropagation(); deleteDetail(d); }}
                      title="Remove from rate"
                    >
                      <IcTrash />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Right: pricing editor */}
        <section className="er-editor">
          {rateRoomTypes[selected] ? (
            <>
              <header className="er-editor__head">
                <div>
                  <span className="er-editor__eyebrow">Editing prices for</span>
                  <h3>{rateRoomTypes[selected].room?.code}</h3>
                </div>
                <button
                  type="button"
                  className="er-btn er-btn--primary"
                  onClick={saveSelected}
                  disabled={submitting}
                >
                  {submitting ? "Saving…" : (<><IcCheck /> Save pricing</>)}
                </button>
              </header>

              <div className="er-price-grid">
                {PRICE_FIELDS.map((f) => (
                  <label className="er-field" key={f.key}>
                    <span>{f.icon} {f.label}</span>
                    <div className="er-input-money">
                      <input
                        inputMode="numeric"
                        value={prices[f.key] ?? 0}
                        onChange={(e) => setPriceField(f.key, e.target.value)}
                        placeholder="15000"
                      />
                      <span className="er-input-money__unit">DA</span>
                    </div>
                  </label>
                ))}
              </div>

              <footer className="er-editor__foot">
                <button
                  type="button"
                  className="er-btn er-btn--ghost"
                  onClick={() => navigate("/rates")}
                >
                  Back to rates
                </button>
                <button
                  type="button"
                  className="er-btn er-btn--primary"
                  onClick={saveSelected}
                  disabled={submitting}
                >
                  {submitting ? "Saving…" : (<><IcCheck /> Save pricing</>)}
                </button>
              </footer>
            </>
          ) : (
            <div className="er-empty">
              <IcEmpty />
              <h4>Pick a room type</h4>
              <p>Select a room type on the left to edit its prices.</p>
            </div>
          )}
        </section>
      </div>

      {activeModal && (
        <AddRoomType
          setActive={setActiveModal}
          rateId={rateId}
          existingRoomIds={existingRoomIds}
        />
      )}
    </div>
  );
};

export default EditRates;
